import React, { useEffect, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { formatDate } from '../../../common/GlobalFunctions';
import { FieldErrors } from '../../../domain/helpers/Errors';
import { validateSchema } from '../../../common/ValidateSchema';
import { AssetInterface } from '../../../models/AssetModel/Asset';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { assetActionSchema } from '../../../domain/ValidationSchemas/Asset/assetAction';
import { AssetActionInterface } from '../../../models/AssetModel/AssetAction/AssetAction';
import ToastMessage from '../../../common/ToastMessage';
import CustomDataGrid from '../../../common/CustomDataGrid';
import useAssetActions from '../../../store/ReactQueries/assets/assetActions/useAssetActions';
import useAddAssetActions from '../../../store/ReactQueries/assets/assetActions/useAddAssetAction';
import useUpdateAssetAction from '../../../store/ReactQueries/assets/assetActions/useUpdateAssetAction';
import useDeleteAssetAction from '../../../store/ReactQueries/assets/assetActions/useDeleteAssetAction';

interface AssetActionsProps {
    assetById: AssetInterface | undefined;
}

export const AssetActions: React.FC<AssetActionsProps> = ({ assetById }) => {

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<AssetActionInterface>({
        id: '',
        title: '',
        assetActionTypeId: '',
        amount: 0,
        date: undefined,
        description: '',
        assetId: assetById?.id ?? ''
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({ add: false, edit: false, view: false });

    const { data: assetActions, error: assetActionError, refetch } = useAssetActions(assetById?.id ?? '', page, pageSize);
    const addAssetAction = useAddAssetActions(page, pageSize);
    const updateAssetAction = useUpdateAssetAction(page, pageSize);
    const deleteAssetAction = useDeleteAssetAction(page, pageSize);

    useEffect(() => {
        if (assetActionError) ToastMessage.Failed(`Error: ${assetActionError.message}`);
    }, [assetActionError]);

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'Id',
            type: "string",
            editable: false
        },
        {
            field: 'title',
            headerName: 'Title',
            type: "string",
            editable: false,
            width: 150
        },
        {
            field: 'assetActionTypeId',
            headerName: 'AssetActionTypeId',
            type: "string",
            editable: false
        },
        {
            field: 'assetActionType',
            headerName: 'AssetActionTypes',
            type: "string",
            width: 150,
            editable: false,
            renderCell: ({ row }) => <span>{row.assetActionType ? row.assetActionType.title : ''}</span>,
        },
        {
            field: 'amount',
            headerName: 'Amount',
            type: "number",
            editable: false
        },
        {
            field: 'date',
            headerName: 'Date',
            type: "date",
            editable: false,
            width: 150,
            valueFormatter: formatDate
        },
        {
            field: 'description',
            headerName: 'Description',
            type: "custom",
            editable: false,
            width: 250,
        },
    ];

    const addItems = () => {
        setForm({ ...form, id: '', title: '', assetActionTypeId: '', amount: 0, date: undefined, description: '' });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteAssetAction.mutate(id, {
                onSuccess: () => {
                    refetch();
                    ToastMessage.Success();
                },
                onError: (error) => ToastMessage.Failed(error.message)
            });
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(assetActionSchema, form);

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const { id, title, assetActionTypeId, amount, date, description, assetId } = form;

        const upForm: AssetActionInterface = {
            id,
            date,
            title,
            amount,
            assetId,
            description,
            assetActionTypeId,
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addAssetAction.mutate(formData, {
                onSuccess: () => {
                    refetch();
                    ToastMessage.Success();
                },
                onError: (error) => ToastMessage.Failed(error.message)
            });
        } else {
            updateAssetAction.mutate(upForm, {
                onSuccess: () => {
                    refetch();
                    ToastMessage.Success();
                },
                onError: (error) => ToastMessage.Failed(error.message)
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    return <div className='mt-2'>
        <form
            className='d-flex flex-column gap-2'
            style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}
        >
            <CustomDataGrid
                name={language['AssetActions']}
                rows={assetActions || []}
                form={form}
                page={page}
                modal={modal}
                errors={errors}
                columns={columns}
                pageSize={pageSize}
                setPage={setPage}
                setForm={setForm}
                setModal={setModal}
                addItems={addItems}
                setErrors={setErrors}
                handleSave={handleSave}
                setPageSize={setPageSize}
                handleDelete={handleDelete}
                hideColumns={['id', 'assetActionTypeId']}
                showColumnModal={['title', 'assetActionTypeId', 'amount', 'date', 'description']}
            />
        </form>
        <hr className={darkMode ? 'text-white' : ''} />
    </div>
}
export default AssetActions